import React, { PureComponent, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import pluralize from 'pluralize';
import AddToGroup from 'app/components/AddToGroup';
import Prompt from 'app/components/Prompt';
import Confirm from 'app/components/Confirm';
import Button from 'components/Button';
import { defaultGroupId, MAIN_CONTEXT } from 'reducers';
import ContactSearchTable from 'app/components/SearchTable/ContactSearchTable';
import { contactGroupPath } from 'app/routes';
import DropdownMenu from 'components/base/DropdownMenu';
import MailingLabelsPopup from 'app/components/MailingLabels';
import RenameListModal from '../../components/RenameListModal';
import {
  saveGroupContext,
  selectLoading,
  selectAssignableGroups,
  ContactGroupTypes,
  selectGroupContext,
  exportContacts,
  updateAppendJob,
  updateContactGroup,
  getSearch,
} from 'data/contacts';
import { selectPabEnabled, selectProfile } from 'data/user';
import { selectBatchDialerList, selectUserId } from 'data/user/selectors';
import { getBatchDialerIntegration, setBatchDialerSource } from 'data/user/actions';
import PushtoBatchDialer from '../../../components/PushtoBatchDialer';
import BatchDialerModal from '../../components/BatchdialerModal';
import ClickToDial from '../../../components/ClickToDial';
import BatchDialerAlert from '../../components/BatchdialerAlert';
import ContactImport from '../ContactImport';

import SkipTraceModal from 'app/components/SkipTraceModal';
import InfoAlert from '../../components/InfoAlert';
import { getAppendJobEstimate } from '../../../data/contacts/actions';
import { selectAppendGroups } from '../../../data/contacts';

import { isPropStream } from '../../../utils/brand';
import AddToMarketingListModal from 'app/components/AddToMarketingListModal';
import { selectUsageSection, selectPlanDetails } from '../../../data/user';
import LeaveMessagePopup from '../../AccountNew/modals/LeaveMessage';
import ActivateSubscriptionPopup from '../../AccountNew/modals/ActivateSubscription';
import PurchaseSavesAndExportsPopup from '../../AccountNew/modals/PurchaseSavesAndExports';

import css from './style.scss';
import ActionDropdownMenu from '../../../components/base/ActionDropdownMenu';
import SVG from 'components/base/SVG';
import ContactListControls from '../contactListControls';

class ContactList extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isBatchDialerModalOpen: false,
      showRenameModal: false,
      renameConfig: {
        title: '',
        description: '',
        initialValue: '',
        placeholder: '',
        checkOptions: null,
        onSubmit: null
      }
    };
    this.handleAddToGroupClick = this.handleAddToGroupClick.bind(this);
    this.handleExportClick = this.handleExportClick.bind(this);
    this.handleRenameGroupClick = this.handleRenameGroupClick.bind(this);
    this.handleGenerateMailingLabels = this.handleGenerateMailingLabels.bind(this);
    this.handleSkipTraceClick = this.handleSkipTraceClick.bind(this);
    this.handleSkipTracing = this.handleSkipTracing.bind(this);
    this.handleOpenRenameModal = this.handleOpenRenameModal.bind(this);
    this.handleCloseRenameModal = this.handleCloseRenameModal.bind(this);
    this.handleSaveRename = this.handleSaveRename.bind(this);
  }

  componentDidMount() {
    const { userId, getBatchDialerIntegration, profile } = this.props;

    const userType = profile?.get?.("type");

    // If userId is present on mount and user is FULL or TEAM_FULL, initialize batch dialer integration list
    if (userId && (userType === "FULL" || userType === "TEAM_FULL")) {
      getBatchDialerIntegration(userId);
    }
  }

  componentDidUpdate(prevProps) {
    const { userId, getBatchDialerIntegration, profile } = this.props;

    const userType = profile?.get?.("type");

    // If userId becomes available (or changes) and user is FULL or TEAM_FULL, re-fetch batch dialer integration info
    if (
      userId &&
      userId !== prevProps.userId &&
      (userType === "FULL" || userType === "TEAM_FULL")
    ) {
      getBatchDialerIntegration(userId);
    }
  }

  // Rename Modal Handlers
  handleOpenRenameModal(config) {
    this.setState({
      showRenameModal: true,
      renameConfig: {
        title: config.title || 'Rename',
        description: config.description || config.text || '',
        initialValue: config.initialValue || config.value || '',
        placeholder: config.placeholder || 'Enter name',
        checkOptions: config.checkOptions || null,
        onSubmit: config.onSubmit
      }
    });
  }

  handleCloseRenameModal() {
    this.setState({ 
      showRenameModal: false,
      renameConfig: {
        title: '',
        description: '',
        initialValue: '',
        placeholder: '',
        checkOptions: null,
        onSubmit: null
      }
    });
  }

  handleSaveRename(newName, options) {
    const { renameConfig } = this.state;
    if (renameConfig.onSubmit && newName?.trim()) {
      renameConfig.onSubmit(newName.trim(), options);
    }
    this.handleCloseRenameModal();
  }

  handleRenameGroupClick() {
    const { context, updateContactGroup, updateAppendJob } = this.props;
    const groupId = context.get('id');
    const currentName = context.getIn(['info', 'name']);
    const isAppendType = context.getIn(['info', 'type']) === ContactGroupTypes.APPEND;

    // EXACT same logic as original: use .substr(1) to remove first character
    // Original code: context.get('id').substr(1)
    let id = String(groupId);
    if (id.length > 0) {
      id = id.substr(1); // Remove the first character (whether it's 'A', 'C', or any other letter)
    }

    this.handleOpenRenameModal({
      title: 'Rename Group',
      description: 'Please enter a new name for this group.',
      initialValue: currentName,
      placeholder: 'Enter group name',
      onSubmit: (newName) => {
        if (isAppendType) {
          updateAppendJob({ id: id, name: newName });
        } else {
          updateContactGroup({ id: id, name: newName });
        }
      }
    });
  }

  handleAddToGroupClick() {
    const { context, groups, openAddToMarketingListModal, usageSectionDetails, leaveMessagePopup } = this.props;
    const { overdueAccount } = this.props.profile;
    const { planDetails } = this.props;
    
    if (overdueAccount) {
      const currentPlan = planDetails && planDetails.accountEnrolledPlans && planDetails.accountEnrolledPlans.find(plan => plan.active);
      const total = currentPlan ? currentPlan.quantity : 0;
      this.props.leaveMessagePopup({
        title: "Subscription Past Due",
        message: `Your subscription is past due, and no new properties can be saved. Upgrade now to access the full ${total}.`,
        yesLable: "Upgrade",
        cancelLable: "Cancel",
        yesRedirect: () => {
          this.props.openAccountUpgrade();
        },
      })
    }
    else if (usageSectionDetails?.id && usageSectionDetails?.savesRemaining <= 0) {
      leaveMessagePopup({
        title: "Not Enough Save Credits",
        message: "You don't have enough save credits to perform this action. Please purchase additional credits to continue.",
        yesLable: "Purchase Credits",
        cancelLable: "Cancel",
        yesRedirect: () => {
          this.props.purchaseSavesAndExportsPopup();
        },
      })
    } else {
      openAddToMarketingListModal({
        title: 'Add to Group',
        groups: groups,
        hideSkipTraceOption: true,
        onSave: (name, onComplete) => {
          const updatedContext = context.setIn(['info', 'name'], name);
          this.props.saveGroupContext(updatedContext, ({ response }) => {
            const savedGroup = response.find(g => g.modified);
            onComplete(context.get('selected'));
            if (savedGroup?.id) {
              this.props.history.push(contactGroupPath(savedGroup.id));
            }
          });
        }
      });
    }
  }

  handleExportClick() {
    const { exportContacts, context } = this.props;
    exportContacts(context);
  }

  handleViewSummaryClick() {
    const { openAppendJobSummary, context } = this.props;
    const groupId = context.get('id');

    // EXACT same logic as original: use .substr(1)
    let id = String(groupId);
    if (id.length > 0) {
      id = id.substr(1);
    }

    openAppendJobSummary({ appendJobId: id });
  }

  handleGenerateMailingLabels() {
    const { openMailingLabels, context } = this.props;
    openMailingLabels({ contactSearch: getSearch(context) });
  }

  getContactSearch() {
    const { context } = this.props;

    const selection = context.get("selection");
    const allSelected = context.get("allSelected");
    const selected = context.get("selected");

    const selectedArray = Array.isArray(selection) ? selection : selection?.toJS() || {};

    return {
      id: context.get("id"),
      name: context.getIn(["info", "name"]),
      filters: context.get("filters")?.toJS() || [],
      sortModel: context.get("sortModel")?.toJS() || [],
      allSelected,
      selected,
      selectedIds: allSelected
        ? []
        : Array.isArray(selectedArray)
          ? selectedArray
          : Object.keys(selectedArray).filter(k => selectedArray[k]),
      unselectedIds: allSelected
        ? []
        : Array.isArray(selectedArray)
          ? []
          : Object.keys(selectedArray).filter(k => !selectedArray[k]),
    };
  }

  openBatchDialerModal = () => {
    const { context, setBatchDialerSource, alert } = this.props;
    const selection = context?.get("selection");
    const selected = context?.get("selected");
    const isAllSelected = context?.get("allSelected");

    if (!selected) {
      if (typeof alert === 'function') {
        alert('Please select the record(s) you would like to add.');
      } else {
        window.alert('Please select the record(s) you would like to add.');
      }
      return;
    }

    setBatchDialerSource("CONTACT");

    if (selection && (isAllSelected || (selection.size === context?.get("selected")))) {
      const searchPayload = this.getContactSearch();
      this.setState({
        isBatchDialerModalOpen: true,
        contactSearch: searchPayload,
      });

    }
  };

  closeBatchDialerModal = () => {
    this.setState({ isBatchDialerModalOpen: false });
  };
  
  handleSkipTraceClick() {
    const { context } = this.props;
    const selectedContacts = context.get('data').filter(contact => contact.get('selected'));
    const selectedContactIds = selectedContacts.toJS().map(contact => contact['id']);
    const isNoContactSelected = selectedContactIds.length === 0;
    if(isNoContactSelected) {
      this.props.showAlert({
        title: 'No Records Selected',
        infoText: 'Please select one or more records before performing a Skip Trace.',
      });
      return;
    }
    this.handleSkipTracing(selectedContactIds);
  }

  handleSkipTracing(contactIds){
    this.props.getAppendJobEstimate({ contactIds, appendMode: "BOTH", reprocessEnabled: false },
      ({ response, error }) => {
      if (error) {
        console.error("Error fetching estimate:", error);
        return;
      }
      const { message } = response || {};
      if ( message?.includes("There is an existing append job")) {
        this.props.showAlert({
           title: "Skip Trace in Progress",
           infoText: "There is a Skip Trace currently in progress. Please try again once it is complete.",
        });
      }
      else {
        this.props.skipTraceModal({
          groups: this.props.appendGroups,
          reprocessEnabled: false,
          contactIds,
        });
      }
    }
  );
  }

  shouldRenderClickToDial() {
    const { profile } = this.props;
    if (!profile) return false;

    const userType = profile?.get?.("type");

    return ["FULL", "TEAM_FULL", "TEAM"].includes(userType);
  }

  render() {
    const { loading, openImport, context, getContactInfo, deleteContacts, deleteGroup, onDragStart, onDragEnd, pabEnabled } = this.props;
    const btn = { loading };
    const status = context.getIn(['info', 'status']) === 'COMPLETE' ? 'Complete' : 'Processing';

    const retrieved = context.get('data').size;
    const size = context.get('size');
    const append = context.getIn(['info', 'type']) === ContactGroupTypes.APPEND;
    const selected = context.get('selected');
    const { showRenameModal, renameConfig } = this.state;

    const profile = this.props.profile;

    const isAdmin = !profile.get("subUser");
    const isTeamMember = profile.get("subUser") && !profile.get("scoutUser");
    const isScoutUser = profile.get("subUser") && profile.get("scoutUser");
    const hasPermission = profile.get("allowPushToBatchDialer");

    const userType = profile?.get?.("type");

    const showPushToBatchDialer =
      userType === "FULL" ||
      (userType === "TEAM_FULL" && hasPermission);

    return (
      <div className={css.root}>
        <ContactListControls
          loading={loading}
          selected={selected}
          context={context}
          append={append}
          pabEnabled={pabEnabled}
          size={size}
          retrieved={retrieved}
          status={status}
          openImport={openImport}
          isDefaultGroup = {context.get('id') !== defaultGroupId}
          handleExportClick={this.handleExportClick}
          handleAddToGroupClick={this.handleAddToGroupClick}
          handleRenameGroupClick={this.handleRenameGroupClick}
          deleteContacts={deleteContacts}
          deleteGroup={deleteGroup}
          handleGenerateMailingLabels={this.handleGenerateMailingLabels}
          handleSkipTraceClick={this.handleSkipTraceClick}
          openBatchDialerModal={this.openBatchDialerModal}
          showPushToBatchDialer={showPushToBatchDialer}
        />

        <div className={css.body}>
          <ContactSearchTable context={context} onDragStart={onDragStart} onDragEnd={onDragEnd} fullMode />
        </div>

        {/* Rename Modal */}
        {showRenameModal && (
          <RenameListModal
            isOpen={showRenameModal}
            onClose={this.handleCloseRenameModal}
            onSave={this.handleSaveRename}
            title={renameConfig.title}
            description={renameConfig.description}
            initialValue={renameConfig.initialValue}
            placeholder={renameConfig.placeholder}
            checkOptions={renameConfig.checkOptions}
          />
        )}

        {isPropStream() &&
          ((isAdmin && !this.props.isConnected) ? (
            <BatchDialerModal
              isOpen={this.state.isBatchDialerModalOpen}
              onClose={this.closeBatchDialerModal}
            />
          ) : (!showPushToBatchDialer || !this.props.isConnected) ? (
                <BatchDialerAlert
                  isOpen={this.state.isBatchDialerModalOpen}
                  onClose={this.closeBatchDialerModal}
                  contactIds={this.state.selectedContactIds}
                />
              ) : (
                  <PushtoBatchDialer
                    isOpen={this.state.isBatchDialerModalOpen}
                    onClose={this.closeBatchDialerModal}
                    contacts={this.state.contactSearch}
                  />
              )
          )
        }

        {isPropStream() && this.shouldRenderClickToDial() && (
          <ClickToDial profile={this.props.profile} />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const batchDialerList = selectBatchDialerList(state);
  const isConnected = Array.isArray(batchDialerList) ? batchDialerList.some(item => item.externalSystem === 'BATCH_DIALER') : false;

  return {
    loading: selectLoading(state),
    pabEnabled: selectPabEnabled(state),
    groups: selectAssignableGroups(state),
    context: selectGroupContext(MAIN_CONTEXT)(state),
    appendGroups: selectAppendGroups(state),
    profile: selectProfile(state),
    isConnected,
    batchDialerList,
    userId: selectUserId(state),
  };
};


const mapActionToProps = {
  saveGroupContext,
  exportContacts,
  updateAppendJob,
  updateContactGroup,
  getBatchDialerIntegration,
  setBatchDialerSource,
  openImport: ContactImport.open,
  openAddToGroup: AddToGroup.open,
  openMailingLabels: MailingLabelsPopup.open,
  prompt: Prompt.open,
  alert: Confirm.alert,
  showAlert:InfoAlert.open,
  skipTraceModal: SkipTraceModal.open,
  openAddToMarketingListModal: AddToMarketingListModal.open,
  leaveMessagePopup: LeaveMessagePopup.open,
  openAccountUpgrade: ActivateSubscriptionPopup.open,
  purchaseSavesAndExportsPopup: PurchaseSavesAndExportsPopup.open,
  getAppendJobEstimate,
};


export default withRouter(connect(mapStateToProps, mapActionToProps)(ContactList));
